import * as React from 'react';
import {
  Heading,
  Spinner,
  Surface,
  Text,
  useToast,
} from '@quality24/design-system';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import {
  storeAdmissionId,
  storeEmbed,
  storeLoginId,
  storeUserType,
} from '@/inpatient-patient-pwa/services/storage';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import { ReactComponent as ErrorImage } from './error_image.svg';

import styles from './IntegrationLoginPage.module.scss';

export interface Props {
  authenticatedContext?: boolean;
}

/**
 * <IntegrationLoginPage> component
 */
const IntegrationLoginPage: React.FunctionComponent<Props> = ({
  authenticatedContext = false,
}) => {
  const { t } = useTranslation();
  const { error: toastError } = useToast();
  const { search } = useLocation();
  const navigate = useNavigate();

  const { integrationLogin, logout } = useAuth();

  // Search params
  const token = new URLSearchParams(search).get('token');
  const admissionId = new URLSearchParams(search).get('admissionId');
  const embed = new URLSearchParams(search).get('embed') === 'true';

  // States
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token || !admissionId) {
      navigate('/login');
      return;
    }

    if (authenticatedContext) {
      logout();
    }

    integrationLogin(token, admissionId)
      .then((data) => {
        // Update storage
        storeUserType('PATIENT');
        storeLoginId(
          data.data?.authFlow?.authenticationResult?.userData?.loginId,
        );
        storeAdmissionId(
          data.data?.authFlow?.authenticationResult?.userData?.admissionId,
        );
        storeEmbed(embed);

        navigate('/');
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error on login', err);

        if (err.message.includes('This admission has ended')) {
          setError(t('integration.notFound'));
        } else {
          setError(t('errors.loadAdmission'));
        }
      })
      .finally(() => setLoading(false));
  }, [
    admissionId,
    authenticatedContext,
    embed,
    integrationLogin,
    logout,
    navigate,
    toastError,
    token,
    t,
  ]);

  return (
    <div className={styles.wrapper}>
      <Helmet>
        <title>Quality24 Concierge</title>
      </Helmet>

      <div className={styles.content}>
        {loading && (
          <Surface>
            <Spinner className="mb-5" size="sm" />
            <Text size="lg1" className="mb-5">
              {t('integration.loading')}
            </Text>
          </Surface>
        )}

        {error && (
          <>
            <Heading as="h1" className="mb-2" weight="bold">
              {error}
            </Heading>

            <ErrorImage width="280px" className="mx-auto mt-4" />
          </>
        )}
      </div>
    </div>
  );
};
export default IntegrationLoginPage;
